<script setup lang="ts">
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
</script>

<template>
  <!-- Transaction Accounts Section -->
  <div id="transaction-accounts-section">
    <main id="transaction-main" class="flex-1 p-5 overflow-y-auto">
      <!-- Summary Cards -->
      <div
        id="summary-section"
        class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
      >
        <div class="bg-white p-5 rounded-md border border-light-gray">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">{{ $t('government.administration.transactionAccounts.summary.totalAllocation') }}</p>
              <p class="text-2xl font-medium text-dark-navy">30%</p>
            </div>
            <i class="fa-solid fa-percentage text-forest-green text-xl"></i>
          </div>
        </div>
        <div class="bg-white p-5 rounded-md border border-light-gray">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">{{ $t('government.administration.transactionAccounts.summary.activeSectors') }}</p>
              <p class="text-2xl font-medium text-dark-navy">8</p>
            </div>
            <i class="fa-solid fa-industry text-electric-blue text-xl"></i>
          </div>
        </div>
        <div class="bg-white p-5 rounded-md border border-light-gray">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">{{ $t('government.administration.transactionAccounts.summary.remaining') }}</p>
              <p class="text-2xl font-medium text-error-red">5%</p>
            </div>
            <i class="fa-solid fa-chart-pie text-error-red text-xl"></i>
          </div>
        </div>
      </div>

      <!-- Global Settings -->
      <div
        id="global-settings"
        class="bg-white p-5 rounded-md border border-light-gray mb-6"
      >
        <h3 class="text-lg font-medium text-dark-navy mb-4">
          {{ $t('government.administration.transactionAccounts.globalSettings.title') }}
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <label class="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="all-sectors"
                  class="rounded border-light-gray"
                />
                <span class="font-medium">{{ $t('government.administration.transactionAccounts.globalSettings.applyToAllSectors') }}</span>
              </label>
            </div>
            <div class="flex items-center space-x-3">
              <label class="text-sm font-medium w-32">{{ $t('government.administration.transactionAccounts.globalSettings.percentage') }}</label>
              <input
                type="number"
                class="flex-1 px-3 py-2 border border-light-gray rounded-md"
                value="30"
                min="0"
                max="100"
              />
              <span class="text-sm text-gray-600">%</span>
            </div>
          </div>
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <label class="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="sector-specific"
                  class="rounded border-light-gray"
                  checked
                />
                <span class="font-medium">{{ $t('government.administration.transactionAccounts.globalSettings.sectorSpecificAllocation') }}</span>
              </label>
            </div>
            <div class="flex justify-end space-x-3">
              <button
                class="px-4 py-2 border border-light-gray rounded-md text-sm hover:bg-gray-50"
              >
                {{ $t('government.administration.transactionAccounts.globalSettings.buttons.reset') }}
              </button>
              <button
                class="px-4 py-2 bg-forest-green text-white rounded-md text-sm hover:bg-green-700"
              >
                {{ $t('government.administration.transactionAccounts.globalSettings.buttons.applyGlobal') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sector Allocation Table -->
      <div
        id="sector-allocation"
        class="bg-white rounded-md border border-light-gray"
      >
        <div class="p-5 border-b border-light-gray">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-medium text-dark-navy">
              {{ $t('government.administration.transactionAccounts.sectorAllocation.title') }}
            </h3>
            <div class="flex space-x-3">
              <button
                class="px-4 py-2 border border-light-gray rounded-md text-sm hover:bg-gray-50"
              >
                <i class="fa-solid fa-plus mr-2"></i>{{ $t('government.administration.transactionAccounts.sectorAllocation.buttons.addSector') }}
              </button>
              <button
                class="px-4 py-2 bg-forest-green text-white rounded-md text-sm hover:bg-green-700"
              >
                <i class="fa-solid fa-save mr-2"></i>{{ $t('government.administration.transactionAccounts.sectorAllocation.buttons.saveChanges') }}
              </button>
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-5 py-3 text-left text-sm font-medium text-gray-700"
                >
                  <input type="checkbox" class="rounded border-light-gray" />
                </th>
                <th
                  class="px-5 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.transactionAccounts.sectorAllocation.table.headers.sectorName') }}
                </th>
                <th
                  class="px-5 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.transactionAccounts.sectorAllocation.table.headers.currentAllocation') }}
                </th>
                <th
                  class="px-5 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.transactionAccounts.sectorAllocation.table.headers.newAllocation') }}
                </th>
                <th
                  class="px-5 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.transactionAccounts.sectorAllocation.table.headers.status') }}
                </th>
                <th
                  class="px-5 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.transactionAccounts.sectorAllocation.table.headers.actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-light-gray">
              <tr class="hover:bg-gray-50">
                <td class="px-5 py-4">
                  <input type="checkbox" class="rounded border-light-gray" />
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center">
                    <i
                      class="fa-solid fa-solar-panel text-forest-green mr-3"
                    ></i>
                    <span class="font-medium">{{ $t('government.administration.transactionAccounts.sectorAllocation.sectors.renewableEnergy') }}</span>
                  </div>
                </td>
                <td class="px-5 py-4 text-sm text-gray-600">8%</td>
                <td class="px-5 py-4">
                  <input
                    type="number"
                    class="w-20 px-2 py-1 border border-light-gray rounded text-sm"
                    value="8"
                    min="0"
                    max="100"
                  />
                </td>
                <td class="px-5 py-4">
                  <span
                    class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                    >{{ $t('government.administration.transactionAccounts.sectorAllocation.status.active') }}</span
                  >
                </td>
                <td class="px-5 py-4">
                  <button class="text-forest-green hover:text-green-700 mr-3">
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button class="text-error-red hover:text-red-700">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
              <tr class="hover:bg-gray-50">
                <td class="px-5 py-4">
                  <input type="checkbox" class="rounded border-light-gray" />
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center">
                    <i class="fa-solid fa-tree text-forest-green mr-3"></i>
                    <span class="font-medium">{{ $t('government.administration.transactionAccounts.sectorAllocation.sectors.forestryLandUse') }}</span>
                  </div>
                </td>
                <td class="px-5 py-4 text-sm text-gray-600">12%</td>
                <td class="px-5 py-4">
                  <input
                    type="number"
                    class="w-20 px-2 py-1 border border-light-gray rounded text-sm"
                    value="12"
                    min="0"
                    max="100"
                  />
                </td>
                <td class="px-5 py-4">
                  <span
                    class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                    >{{ $t('government.administration.transactionAccounts.sectorAllocation.status.active') }}</span
                  >
                </td>
                <td class="px-5 py-4">
                  <button class="text-forest-green hover:text-green-700 mr-3">
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button class="text-error-red hover:text-red-700">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
              <tr class="hover:bg-gray-50">
                <td class="px-5 py-4">
                  <input type="checkbox" class="rounded border-light-gray" />
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center">
                    <i class="fa-solid fa-seedling text-forest-green mr-3"></i>
                    <span class="font-medium">{{ $t('government.administration.transactionAccounts.sectorAllocation.sectors.agriculture') }}</span>
                  </div>
                </td>
                <td class="px-5 py-4 text-sm text-gray-600">3%</td>
                <td class="px-5 py-4">
                  <input
                    type="number"
                    class="w-20 px-2 py-1 border border-light-gray rounded text-sm"
                    value="3"
                    min="0"
                    max="100"
                  />
                </td>
                <td class="px-5 py-4">
                  <span
                    class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                    >{{ $t('government.administration.transactionAccounts.sectorAllocation.status.active') }}</span
                  >
                </td>
                <td class="px-5 py-4">
                  <button class="text-forest-green hover:text-green-700 mr-3">
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button class="text-error-red hover:text-red-700">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
              <tr class="hover:bg-gray-50">
                <td class="px-5 py-4">
                  <input type="checkbox" class="rounded border-light-gray" />
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center">
                    <i class="fa-solid fa-recycle text-forest-green mr-3"></i>
                    <span class="font-medium">{{ $t('government.administration.transactionAccounts.sectorAllocation.sectors.wasteManagement') }}</span>
                  </div>
                </td>
                <td class="px-5 py-4 text-sm text-gray-600">2%</td>
                <td class="px-5 py-4">
                  <input
                    type="number"
                    class="w-20 px-2 py-1 border border-light-gray rounded text-sm"
                    value="2"
                    min="0"
                    max="100"
                  />
                </td>
                <td class="px-5 py-4">
                  <span
                    class="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                    >{{ $t('government.administration.transactionAccounts.sectorAllocation.status.pending') }}</span
                  >
                </td>
                <td class="px-5 py-4">
                  <button class="text-forest-green hover:text-green-700 mr-3">
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button class="text-error-red hover:text-red-700">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
              <tr class="hover:bg-gray-50">
                <td class="px-5 py-4">
                  <input type="checkbox" class="rounded border-light-gray" />
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center">
                    <i class="fa-solid fa-car text-forest-green mr-3"></i>
                    <span class="font-medium">{{ $t('government.administration.transactionAccounts.sectorAllocation.sectors.transportation') }}</span>
                  </div>
                </td>
                <td class="px-5 py-4 text-sm text-gray-600">0%</td>
                <td class="px-5 py-4">
                  <input
                    type="number"
                    class="w-20 px-2 py-1 border border-light-gray rounded text-sm"
                    value="0"
                    min="0"
                    max="100"
                  />
                </td>
                <td class="px-5 py-4">
                  <span
                    class="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs"
                    >{{ $t('government.administration.transactionAccounts.sectorAllocation.status.inactive') }}</span
                  >
                </td>
                <td class="px-5 py-4">
                  <button class="text-forest-green hover:text-green-700 mr-3">
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button class="text-error-red hover:text-red-700">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="p-5 border-t border-light-gray bg-gray-50">
          <div class="flex justify-between items-center">
            <div class="text-sm text-gray-600">
              {{ $t('government.administration.transactionAccounts.footer.totalAllocated') }}
              <span class="font-medium text-dark-navy">25%</span> | {{ $t('government.administration.transactionAccounts.footer.remaining') }}
              <span class="font-medium text-error-red">5%</span>
            </div>
            <div class="flex space-x-3">
              <button
                class="px-4 py-2 border border-light-gray rounded-md text-sm hover:bg-gray-50"
              >
                {{ $t('government.administration.transactionAccounts.footer.buttons.cancel') }}
              </button>
              <button
                class="px-4 py-2 bg-forest-green text-white rounded-md text-sm hover:bg-green-700"
              >
                {{ $t('government.administration.transactionAccounts.footer.buttons.saveAllChanges') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
